import {
  createFileRoute,
  Navigate,
  getRouteApi,
  useRouter,
} from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '@lingui/core/macro';
import { settingsService } from '@/services/settings';
import type { KindleSettingsUpdate } from '@/types/settings';
import KindleSettings from '@/components/Settings/KindleSettings';
import { useToast } from '@/hooks/useToast';
import { showFlashMessage } from '@/utils/flashMessageUtils';

const settingsRoute = getRouteApi('/_app/settings');

export const Route = createFileRoute('/_app/settings/kindle')({
  component: KindleSettingsPage,
});

function KindleSettingsPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const settings = settingsRoute.useLoaderData();
  const { showToast } = useToast();
  const formKey = settings.user.formKey;

  const updateKindleSettingsMutation = useMutation({
    mutationFn: ({
      update,
      formKey,
    }: {
      update: KindleSettingsUpdate;
      formKey: string;
    }) => settingsService.updateKindleSettings(update, formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      await router.invalidate();
      queryClient.invalidateQueries({ queryKey: ['user', 'settings'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  const sendKindleNowMutation = useMutation({
    mutationFn: (formKey: string) => settingsService.sendKindleNow(formKey),
    onSuccess: async () => {
      // Check for flash message from backend (with delay for fetch redirects)
      showFlashMessage(showToast);
      queryClient.invalidateQueries({ queryKey: ['user', 'settings'] });
    },
    onError: (error) => {
      showToast(
        error instanceof Error
          ? error.message
          : t`Sorry, there was an error. Please try again.`,
        'error',
      );
    },
  });

  // Redirect to account if no Kindle settings
  if (!settings.kindleSettings) {
    return <Navigate to="/settings/account" replace />;
  }

  return (
    <KindleSettings
      settings={settings.kindleSettings}
      onUpdate={(update: KindleSettingsUpdate) => {
        updateKindleSettingsMutation.mutate({ update, formKey });
      }}
      onSendNow={() => {
        sendKindleNowMutation.mutate(formKey);
      }}
      isSending={sendKindleNowMutation.isPending}
    />
  );
}
